import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const PACKAGES = [
  {
    id: "free",
    name: "Free",
    price: "—",
    cadence: "always",
    chip: "The wanderer's purse",
    benefits: [
      "Browse all listings (hosts & hermits)",
      "Read the manifesto, no obligation",
      "Create a basic profile",
      "Receive welcome letter",
    ],
    cta: "Continue free",
  },
  {
    id: "starter",
    name: "Starter",
    price: "$1",
    cadence: "for 7 days, then $4.99/mo",
    chip: "A short residence",
    benefits: [
      "Everything in Free",
      "Send up to 5 letters of intent",
      "Message matched parties",
      "Save listings to a ledger",
    ],
    cta: "Try for $1",
  },
  {
    id: "standard",
    name: "Standard",
    price: "$4.99",
    cadence: "per month",
    chip: "The settled hermit",
    benefits: [
      "Everything in Starter",
      "Unlimited letters of intent",
      "Featured profile in searches",
      "Priority in host inboxes",
      "Sealed-letter custom themes",
    ],
    cta: "Subscribe monthly",
  },
  {
    id: "annual",
    name: "Annual",
    price: "$35.99",
    cadence: "per year · Save 40%",
    chip: "The lifelong recluse",
    badge: "Most chosen",
    benefits: [
      "Everything in Standard",
      "12 months for the price of ~7",
      "Annual “Letter from the Estate” keepsake",
      "Founding-member badge (woodcut stamp)",
      "Early access to new regions",
    ],
    cta: "Subscribe annually",
  },
];

export default function Pricing() {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const onSubscribe = async (pkgId) => {
    setError("");
    if (pkgId === "free") {
      navigate(user ? "/dashboard" : "/sign-up");
      return;
    }
    if (!user) {
      navigate("/sign-up");
      return;
    }
    setPending(pkgId);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/subscription/checkout", { package_id: pkgId, origin_url: origin });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPending(null);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
      <div className="text-center max-w-2xl mx-auto">
        <p className="label-overline">The ledger of arrangements</p>
        <h1 className="font-display text-5xl sm:text-6xl mt-3">Four ways to belong</h1>
        <div className="fleuron my-6">❦ ❦ ❦</div>
        <p className="font-body text-ink-iron text-lg">
          Each tier includes everything from the tier before. Cancel any time, in writing.
        </p>
      </div>

      {error && <p className="text-wax-red text-center mt-6 italic">{error}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
        {PACKAGES.map((p) => (
          <div
            key={p.id}
            data-testid={`pricing-card-${p.id}`}
            className={`parchment-card flex flex-col ${p.id === "annual" ? "border-wax-red border-2" : ""}`}
          >
            <div className="flex items-center justify-between">
              <p className="label-overline">{p.name}</p>
              {p.badge && <span className="label-overline text-wax-red">{p.badge}</span>}
            </div>
            <p className="font-display italic text-ink-marginalia mt-2">{p.chip}</p>
            <p className="font-display text-5xl mt-5">{p.price}</p>
            <p className="font-body text-sm text-ink-iron mt-1">{p.cadence}</p>
            <ul className="mt-6 space-y-2 font-body text-sm text-ink-iron flex-1">
              {p.benefits.map((b) => (
                <li key={b} className="flex gap-2">
                  <span className="text-wax-red">❦</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid={`pricing-cta-${p.id}`}
              onClick={() => onSubscribe(p.id)}
              disabled={pending === p.id}
              className={`mt-7 ${p.id === "annual" ? "wax-button" : "ghost-button"} w-full`}
            >
              {pending === p.id ? "Preparing the parchment…" : p.cta}
            </button>
          </div>
        ))}
      </div>

      <div className="max-w-2xl mx-auto mt-14 text-center font-body text-ink-marginalia text-sm italic">
        Prices in USD. Stripe handles secure billing for USA and UK customers. You may cancel any subscription from your dashboard.
      </div>
      <p className="text-center mt-3 text-sm text-ink-marginalia">
        <Link to="/dashboard" className="hover:text-wax-red">Manage your arrangement →</Link>
      </p>
    </main>
  );
}
